import { update } from '@intercom/messenger-js-sdk'
import { useEffect } from 'react'
import { useStartupContext } from '../contexts/startup-guard'
import { useHomeStateQuery } from '../features/home/hooks/use-home-state'
import { useAnalyticsQuery } from '../hooks/api'
import { useAppSession } from '../hooks/use-app-session'
import { useFounderEngagement } from '../hooks/use-founder-engagement'

// Profile-view count changes slowly and is only used for survey targeting, so we
// fetch it once and don't refetch on window focus — keeps this app-wide mount from
// re-running the analytics aggregation on every refocus.
const ANALYTICS_STALE_MS = 5 * 60_000

/**
 * Intercom reads dates as unix **seconds**, and infers an attribute's type from
 * its first value — a stamp sent once as an ISO string is stuck as a string,
 * with no date filters, and cannot be converted without Intercom support.
 */
const toIntercomDate = (iso: string) => Math.floor(Date.parse(iso) / 1000)

/**
 * Pushes the attributes our Intercom audiences target on.
 *
 * Engagement breadth and depth:
 *  - `sf_has_evaluation` — founder reached the core "aha" (deck + evaluation)
 *  - `sf_tabs_visited` — distinct top-level tabs visited
 *  - `sf_active_minutes` — active minutes in app
 *  - `sf_profile_views` — unique investor visits to the founder's data room
 *
 * The PMF-pulse rule (fire between 24h and 14d after profile completion, on the
 * second or later session, on a screen that is not on fire, never to us):
 *  - `sf_profile_completed_at` — unix seconds, so the audience can say
 *    "more than 1 day ago" and "less than 14 days ago"
 *  - `sf_sessions_since_completion` — the founder came back
 *  - `sf_home_is_alert` — their Home is showing bad news right now
 *
 * Our own accounts are excluded in the dashboard, by email and company, rather
 * than by an attribute the app has to keep in step with a list of ids.
 *
 * All of it is targeting input only: the thresholds, the display rules and the
 * "once per person" frequency live in Intercom's audience rules, keyed to the
 * founder's identity via the JWT — so dedupe is account-wide and cross-device,
 * which no device-local count can be. Intercom decides if and when a survey
 * fires.
 *
 * The engagement counts are device-local (localStorage, never logged to our
 * servers). `sf_profile_views` is different: it mirrors up already-collected
 * server-side analytics — how the *audience* engages with the profile, not the
 * founder's own activity — so we can target follow-ups (e.g. a "why haven't you
 * shared?" survey) at founders whose profile has few or no views.
 *
 * Renders nothing; mount once inside the startup context (the messenger boots
 * at the app root, so `update` merges into the live identified session).
 */
export function IntercomFounderAttributes() {
  const { hasEvaluation, startup, user } = useStartupContext()
  const { tabsVisited, activeMinutes } = useFounderEngagement(user.id, startup.id)

  // The wire carries an ISO string; epoch ms downstream, so a refetch handing
  // back an equal-but-new value does not restart the session effect.
  const completedAt = startup.profileCompletedAt ? Date.parse(startup.profileCompletedAt) : null
  const sessionsSinceCompletion = useAppSession(user.id, completedAt)

  // Shared key with Home and the sidebar CTA, which read it on every founder
  // page — so this mount adds no request of its own.
  const { data: homeData } = useHomeStateQuery(startup.id)
  const home = homeData?.data

  // Unique investor visitors to this founder's data room (owner-scoped analytics).
  const { data: analyticsData } = useAnalyticsQuery(startup.id, {
    staleTime: ANALYTICS_STALE_MS,
    refetchOnWindowFocus: false,
  })
  const profileViews = analyticsData?.data?.visitors?.length

  const isAlert = home ? home.isAlert || home.banners.length > 0 : undefined

  useEffect(() => {
    update({
      sf_has_evaluation: hasEvaluation,
      sf_tabs_visited: tabsVisited,
      sf_active_minutes: activeMinutes,
      sf_sessions_since_completion: sessionsSinceCompletion,
      ...(startup.profileCompletedAt && {
        sf_profile_completed_at: toIntercomDate(startup.profileCompletedAt),
      }),
      // Both of these are sent only once known, so a transient undefined never
      // overwrites a real value with a misleading default — 0 views and "not
      // alerting" are each meaningful targeting signals in their own right.
      ...(isAlert !== undefined && { sf_home_is_alert: isAlert }),
      ...(profileViews !== undefined && { sf_profile_views: profileViews }),
    })
  }, [
    hasEvaluation,
    tabsVisited,
    activeMinutes,
    profileViews,
    sessionsSinceCompletion,
    isAlert,
    startup.profileCompletedAt,
  ])

  return null
}
